import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ApolloCache, useApolloClient } from '@apollo/client';
import { GetItemDataByIdQuery } from 'packages/shared/definitions/types';
import { useStore } from 'zustand';

import {
  ActionButton,
  ButtonModel,
  IconSolidInfo,
  Text,
  Toaster,
} from '@notino/react-styleguide';

import { Flex } from '@components/Flex';
import getItemDataByIdQuery from '@components/Universals/ProductItem/queries/itemDataById.graphql';
import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import { useProductsByIdsBatched } from '@containers/ProductDetailContainer/ProductDetail/ProductsSection/useProductsByIdsBatched';
import { AttributeTypes } from '@containers/ProductListing/model';
import { trackMultiAddToCart } from '@containers/WishlistLists/tracking';
import { ProductEventWither } from '@helpers/googleTagManager';
import { useAddToCartMutation } from '@sharedComponents/AddToCartButton/context/useAddToCartMutation';
import cartMessages from '@sharedComponents/AddToCartButton/messages';
import { PostAddToCartAction } from '@sharedComponents/AddToCartButton/model';
import { updateHeaderCartOverview } from '@sharedComponents/AddToCartButton/sharedComponents/BuyButton/utils';

import { useCloseListMenuDrawer, useListMenuContext } from '../../context';
import messages, { listNotificationMessages } from '../../messages';

import {
  Buttons,
  Footer,
  ProfessionalProductsWarning,
  Wrapper,
} from './styled';

export const ADD_ALL_TO_CART_MAX_WISHLIST_ITEMS = 10;

export const AddAllToCart = () => {
  const closeDrawer = useCloseListMenuDrawer();
  const { store } = useListMenuContext();
  const { list } = useStore(store);
  const { addToCartMutation, loading, error } = useAddToCartMutation();

  const handleAddToCart = async () => {
    await addToCartMutation({
      variables: {
        products: list.products.map((productId) => ({
          count: 1,
          productId,
        })),
      },
      onCompleted: () => {
        Toaster.info({
          icon: 'IconRegularCheckmark',
          content: (
            <FormattedMessage {...listNotificationMessages.itemsAddedToCard} />
          ),
        });
        updateHeaderCartOverview({
          cartSettings: PostAddToCartAction.ONLY_ADD,
        });
        closeDrawer();
      },
      update: (cache: ApolloCache<object>) => {
        const vpProducts = list.products
          .map((id) => {
            return cache.readQuery<GetItemDataByIdQuery>({
              query: getItemDataByIdQuery,
              variables: { id },
            })?.vpProductById;
          })
          ?.filter(Boolean);

        const isDefaultList = list.id === useWishlistLists.DEFAULT_LIST_ID;

        const trackingProducts = vpProducts.map((product) =>
          ProductEventWither()
            .withVpProduct(product)
            .withAdditionalData({
              wishlist_type: isDefaultList ? 'default' : 'custom',
            })
            .build()
        );

        trackMultiAddToCart({
          productCount: list.products.length,
          products: trackingProducts,
        });
      },
    });
  };

  const hasProfessionalProduct = useHasProfessionalProduct(list.products);

  return (
    <Wrapper>
      <Text typography="bodyRegular" color="text.secondary">
        <FormattedMessage {...messages.addAllToCartDrawerText} />
      </Text>

      {hasProfessionalProduct && (
        <ProfessionalProductsWarning>
          <IconSolidInfo color="icon.tertiary" />

          <Flex direction="column" gap="4px">
            <Text typography="labelRegular" color="text.primary">
              <FormattedMessage
                {...messages.addAllToCartProffesionalProductsWarningTitle}
              />
            </Text>
            <Text typography="bodyRegular" color="text.secondary">
              <FormattedMessage
                {...cartMessages.productForProfessionalAcknowledgement}
              />
            </Text>
          </Flex>
        </ProfessionalProductsWarning>
      )}

      <Footer>
        <Buttons>
          <ActionButton
            onClick={handleAddToCart}
            isLoading={loading}
            error={!!error}
          >
            <FormattedMessage {...messages.addAllToCartDrawerYesButton} />
          </ActionButton>

          <ActionButton
            buttonStyle={ButtonModel.Styles.secondary}
            onClick={() => store.setState({ currentAction: 'rootMenu' })}
          >
            <FormattedMessage {...messages.addAllToCartDrawerNoButton} />
          </ActionButton>
        </Buttons>
      </Footer>
    </Wrapper>
  );
};

const useHasProfessionalProduct = (productIds: number[]) => {
  const { cache } = useApolloClient();
  const cachedProducts = productIds.map(
    (id) =>
      cache.readQuery<GetItemDataByIdQuery>({
        query: getItemDataByIdQuery,
        variables: { id },
      })?.vpProductById
  );

  const wereAllProductsCached = cachedProducts.every(Boolean);

  const { data } = useProductsByIdsBatched(productIds, {
    skip: wereAllProductsCached,
  });

  const productsToCheck = wereAllProductsCached
    ? cachedProducts
    : data?.vpProductByIds;

  return productsToCheck?.some(
    (p) => p.attributes[AttributeTypes.IsForProfessionals]
  );
};
